'use client'

import React, { useState } from 'react';
import { 
  FaRegSadTear, 
  FaRegSmile, 
  FaRegFrown, 
  FaInfoCircle, 
  FaQuestionCircle,
  FaBook,
  FaLightbulb,
  FaLeaf
} from 'react-icons/fa';
import { Line } from 'react-chartjs-2';
import { Chart as ChartJS, CategoryScale, LinearScale, PointElement, LineElement, Title, Tooltip, Legend } from 'chart.js';
import { Helmet } from 'react-helmet';

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend
);

const FoodCalorieEstimator = () => {
  const [inputs, setInputs] = useState({
    mealType: '',
    servings: '',
    ingredients: []
  });
  const [results, setResults] = useState(null);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setInputs(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleIngredientChange = (index, field, value) => {
    const newIngredients = [...inputs.ingredients];
    newIngredients[index] = {
      ...newIngredients[index],
      [field]: value
    };
    setInputs(prev => ({
      ...prev,
      ingredients: newIngredients
    }));
  };

  const addIngredient = () => {
    setInputs(prev => ({
      ...prev,
      ingredients: [...prev.ingredients, { name: '', calories: '' }]
    }));
  };

  const removeIngredient = (index) => {
    const newIngredients = [...inputs.ingredients];
    newIngredients.splice(index, 1);
    setInputs(prev => ({
      ...prev,
      ingredients: newIngredients
    }));
  };

  const estimateCalories = () => {
    if (!inputs.mealType || !inputs.servings || inputs.ingredients.length === 0) return;

    // Simplified calculation for demonstration
    let totalCalories = 0;
    inputs.ingredients.forEach(ingredient => {
      if (ingredient.calories) {
        totalCalories += parseFloat(ingredient.calories);
      }
    });

    const caloriesPerServing = totalCalories / parseFloat(inputs.servings);

    setResults({
      totalCalories,
      caloriesPerServing: caloriesPerServing.toFixed(2),
      mealType: inputs.mealType,
      servings: inputs.servings
    });
  };

  const lineData = {
    labels: inputs.ingredients.map(ingredient => ingredient.name),
    datasets: [
      {
        label: 'Calories per Ingredient',
        data: inputs.ingredients.map(ingredient => parseFloat(ingredient.calories) || 0),
        borderColor: 'rgb(75, 192, 192)',
        tension: 0.1
      }
    ]
  };

  const lineOptions = {
    responsive: true,
    plugins: {
      legend: {
        position: 'top',
      },
      title: {
        display: true,
        text: 'Calorie Distribution by Ingredient'
      }
    },
    scales: {
      y: {
        beginAtZero: true
      }
    }
  };

  return (
    <>
      <Helmet>
        <title>Food Calorie Estimator - Calculate Meal Calories</title>
        <meta name="description" content="Estimate the calorie content of your meals and recipes. Get accurate calorie calculations for different meal types and serving sizes." />
        <meta property="og:title" content="Food Calorie Estimator" />
        <meta property="og:description" content="Estimate the calorie content of your meals and recipes. Get accurate calorie calculations for different meal types and serving sizes." />
        <meta property="og:image" content="https://your-domain.com/food-calorie-estimator-preview.png" />
        <meta property="og:url" content="https://your-domain.com/food-calorie-estimator" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="Food Calorie Estimator" />
        <meta name="twitter:description" content="Estimate the calorie content of your meals and recipes. Get accurate calorie calculations for different meal types and serving sizes." />
        <meta name="twitter:image" content="https://your-domain.com/food-calorie-estimator-preview.png" />
      </Helmet>

      <div className="max-w-4xl mx-auto p-6 bg-white rounded-lg shadow-lg">
        <div className="text-center mb-8">
          <h1 className="text-3xl font-bold text-green-600 mb-2">
            🍽️ Food Calorie Estimator
          </h1>
          <p className="text-gray-600">
            Estimate the calorie content of your meals and recipes
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-8">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Meal Type
            </label>
            <select
              name="mealType"
              value={inputs.mealType}
              onChange={handleInputChange}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500"
            >
              <option value="">Select a meal type</option>
              <option value="breakfast">Breakfast</option>
              <option value="lunch">Lunch</option>
              <option value="dinner">Dinner</option>
              <option value="snack">Snack</option>
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Number of Servings
            </label>
            <input
              type="number"
              name="servings"
              value={inputs.servings}
              onChange={handleInputChange}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500"
              placeholder="Enter number of servings"
            />
          </div>
        </div>

        <div className="mb-8">
          <h2 className="text-xl font-semibold mb-4">Ingredients</h2>
          {inputs.ingredients.map((ingredient, index) => (
            <div key={index} className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Ingredient Name
                </label>
                <input
                  type="text"
                  value={ingredient.name}
                  onChange={(e) => handleIngredientChange(index, 'name', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500"
                  placeholder="Enter ingredient name"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Calories
                </label>
                <input
                  type="number"
                  value={ingredient.calories}
                  onChange={(e) => handleIngredientChange(index, 'calories', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500"
                  placeholder="Enter calories"
                />
              </div>
              <div className="flex items-center justify-end">
                <button
                  onClick={() => removeIngredient(index)}
                  className="text-red-500 hover:text-red-700"
                >
                  Remove Ingredient
                </button>
              </div>
            </div>
          ))}
          <button
            onClick={addIngredient}
            className="w-full bg-green-100 text-green-600 py-2 px-4 rounded-md hover:bg-green-200 transition-colors"
          >
            Add Ingredient
          </button>
        </div>

        <button
          onClick={estimateCalories}
          className="w-full bg-green-600 text-white py-2 px-4 rounded-md hover:bg-green-700 transition-colors mb-8"
          disabled={!inputs.mealType || !inputs.servings || inputs.ingredients.length === 0}
        >
          Estimate Calories
        </button>

        {results && (
          <div className="p-6 bg-green-50 rounded-lg border border-green-200 mb-8">
            <h2 className="text-xl font-semibold mb-4">Calorie Estimation Results</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <h3 className="font-medium">Meal Type</h3>
                <p className="text-green-600 font-semibold">{results.mealType}</p>
              </div>
              <div>
                <h3 className="font-medium">Number of Servings</h3>
                <p className="text-green-600 font-semibold">{results.servings}</p>
              </div>
              <div>
                <h3 className="font-medium">Total Calories</h3>
                <p className="text-green-600 font-semibold">{results.totalCalories.toFixed(2)} calories</p>
              </div>
              <div>
                <h3 className="font-medium">Calories per Serving</h3>
                <p className="text-green-600 font-semibold">{results.caloriesPerServing} calories</p>
              </div>
            </div>
          </div>
        )}

        {results && (
          <div className="mb-8">
            <h2 className="text-xl font-semibold mb-4">Calorie Distribution</h2>
            <div className="h-64">
              <Line data={lineData} options={lineOptions} />
            </div>
          </div>
        )}

        <div className="mt-16">
          <h2 className="text-xl font-semibold mb-4 flex items-center">
            <FaBook className="mr-2" />
            How to Use
          </h2>
          <ol className="list-decimal list-inside text-gray-600 space-y-2">
            <li>Select your meal type (Breakfast, Lunch, Dinner, or Snack)</li>
            <li>Enter the number of servings</li>
            <li>Add ingredients one by one with their calorie content</li>
            <li>Click "Estimate Calories" to see the total and per serving calorie count</li>
            <li>View the calorie distribution graph to understand how calories are distributed among ingredients</li>
          </ol>
        </div>

        <div className="mt-8">
          <h2 className="text-xl font-semibold mb-4 flex items-center">
            <FaLightbulb className="mr-2" />
            Features & Benefits
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="p-4 bg-green-50 rounded-lg">
              <h3 className="text-green-600 font-semibold mb-2">Accurate Calculations</h3>
              <p className="text-gray-600">
                Get precise calorie estimates for your meals and recipes
              </p>
            </div>
            <div className="p-4 bg-green-50 rounded-lg">
              <h3 className="text-green-600 font-semibold mb-2">Multiple Ingredients</h3>
              <p className="text-gray-600">
                Add multiple ingredients with their individual calorie counts
              </p>
            </div>
            <div className="p-4 bg-green-50 rounded-lg">
              <h3 className="text-green-600 font-semibold mb-2">Serving Size Adjustments</h3>
              <p className="text-gray-600">
                Easily adjust serving sizes to get accurate per-serving calorie counts
              </p>
            </div>
            <div className="p-4 bg-green-50 rounded-lg">
              <h3 className="text-green-600 font-semibold mb-2">Meal Type Tracking</h3>
              <p className="text-gray-600">
                Track calories for different meal types throughout the day
              </p>
            </div>
          </div>
        </div>

        <div className="mt-8">
          <h2 className="text-xl font-semibold mb-4 flex items-center">
            <FaQuestionCircle className="mr-2" />
            Frequently Asked Questions
          </h2>
          <div className="space-y-4">
            <div className="border-l-4 border-green-600 pl-4">
              <h3 className="font-medium">How do I find calorie information for ingredients?</h3>
              <p className="text-gray-600">
                You can find calorie information on food packaging, nutritional databases, or online nutrition calculators.
              </p>
            </div>
            <div className="border-l-4 border-green-600 pl-4">
              <h3 className="font-medium">Why is tracking calories important?</h3>
              <p className="text-gray-600">
                Tracking calories helps in maintaining a balanced diet, managing weight, and understanding your nutritional intake.
              </p>
            </div>
            <div className="border-l-4 border-green-600 pl-4">
              <h3 className="font-medium">How accurate are the estimates?</h3>
              <p className="text-gray-600">
                The estimates are based on the information you provide. For more accurate results, use precise calorie measurements from food labels or nutritional databases.
              </p>
            </div>
            <div className="border-l-4 border-green-600 pl-4">
              <h3 className="font-medium">Can I use this for meal planning?</h3>
              <p className="text-gray-600">
                Yes! Use this calculator to plan balanced meals by tracking total calories and calories per serving.
              </p>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default FoodCalorieEstimator;
